import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Printer, SquarePen, Trash2 } from 'lucide-react';
import { deleteShiftRoster, downloadShiftRoster, getShiftRosters } from '../../../services/shiftRoasterService';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../components/Loader/Loader';
import Pagination from '../../../components/Pagination/Pagination';
import ErrorModal from '../../../components/ConfirmModal/ErrorModal';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';

interface ShiftRoasterTableProps {
  reloadTable: number;
  searchQuery?: string;
}

const ShiftRoasterTable = ({ reloadTable, searchQuery }: ShiftRoasterTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [shiftRosters, setShiftRosters] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [downloadingRosterId, setDownloadingRosterId] = useState<number | string | null>(null);
  const [downloadError, setDownloadError] = useState('');
  const [rosterToDownload, setRosterToDownload] = useState<any>(null);
  const [rosterToDelete, setRosterToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [printingRosterId, setPrintingRosterId] = useState<number | string | null>(null);
  const [printError, setPrintError] = useState('');

  const navigate = useNavigate();

  const fetchShiftRosters = async (page = 1, search = '') => {
    try {
      setLoading(true);

      const response = await getShiftRosters(page, search);

      setShiftRosters(response.data || []);

      setTotalPages(response.meta?.last_page || 1);
    } catch (error) {
      console.error('Error fetching shift rosters:', error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, reloadTable]);

  useEffect(() => {
    fetchShiftRosters(currentPage, searchQuery);
  }, [currentPage, reloadTable, searchQuery]);

  const getMonthYear = (month: number, year: string) => {
    return new Date(Number(year), month - 1).toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });
  };

  const openDownloadConfirmation = (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
    event.stopPropagation();
    setRosterToDownload(row);
  };

  const handleDownload = async () => {
    if (!rosterToDownload || downloadingRosterId !== null) return;

    try {
      setDownloadingRosterId(rosterToDownload.id);

      const pdfBlob = await downloadShiftRoster(rosterToDownload.id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      const referenceNumber = rosterToDownload.reference_number || rosterToDownload.reference_no || `shift-roster-${rosterToDownload.id}`;

      link.href = url;
      link.download = `${referenceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      setRosterToDownload(null);
    } catch (error: any) {
      let message = error?.message || 'Failed to download shift roster.';
      const responseData = error?.response?.data;

      if (responseData instanceof Blob) {
        try {
          const parsedResponse = JSON.parse(await responseData.text());
          message = parsedResponse?.message || parsedResponse?.error || message;
        } catch {
          // Keep the fallback message when the Blob is not JSON.
        }
      } else {
        message = responseData?.message || responseData?.error || message;
      }

      setRosterToDownload(null);
      setDownloadError(message);
    } finally {
      setDownloadingRosterId(null);
    }
  };

  const openDeleteConfirmation = (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
    event.stopPropagation();
    setRosterToDelete(row);
  };

  const handleDelete = async () => {
    if (!rosterToDelete || deleteLoading) return;

    try {
      setDeleteLoading(true);
      await deleteShiftRoster(rosterToDelete.id);
      setRosterToDelete(null);
      await fetchShiftRosters(currentPage, searchQuery);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to delete shift roster.';

      setRosterToDelete(null);
      setDeleteError(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePrint = async (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
    event.stopPropagation();

    if (printingRosterId !== null) return;

    try {
      setPrintingRosterId(row.id);

      const pdfBlob = await downloadShiftRoster(row.id);
      const url = window.URL.createObjectURL(pdfBlob);
      const printFrame = document.createElement('iframe');

      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      printFrame.src = url;

      printFrame.onload = () => {
        printFrame.contentWindow?.focus();
        printFrame.contentWindow?.print();
        setPrintingRosterId(null);
      };

      document.body.appendChild(printFrame);

      window.setTimeout(() => {
        printFrame.remove();
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (error: any) {
      let message = error?.message || 'Failed to print shift roster.';
      const responseData = error?.response?.data;

      if (responseData instanceof Blob) {
        try {
          const parsedResponse = JSON.parse(await responseData.text());
          message = parsedResponse?.message || parsedResponse?.error || message;
        } catch {
          // Keep the fallback message when the Blob is not JSON.
        }
      } else {
        message = responseData?.message || responseData?.error || message;
      }

      setPrintError(message);
      setPrintingRosterId(null);
    }
  };

  return (
    <div className="px-6 py-4">
      <div className="border border-[#e4e9f0] rounded-lg overflow-hidden">
        {loading ? (
          <Loader />
        ) : (
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-white">
                <th className="px-4 py-3 border border-[#e4e9f0] text-left text-sm font-medium text-gray-500">SL. No.</th>
                <th className="px-6 py-3 border border-[#e4e9f0] text-left text-sm font-medium text-gray-500">Ref No.</th>
                <th className="px-6 py-3 border border-[#e4e9f0] text-left text-sm font-medium text-gray-500">Month/Year</th>
                <th className="px-6 py-3 border border-[#e4e9f0] text-left text-sm font-medium text-gray-500">Date</th>
                <th className="px-6 py-3 border border-[#e4e9f0] text-left text-sm font-medium text-gray-500">Compiled By</th>
                <th className="px-6 py-3 border border-[#e4e9f0] text-left text-sm font-medium text-gray-500">Status</th>
                <th className="px-6 py-3 border border-[#e4e9f0] text-left text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>

            <tbody>
              {shiftRosters.map((row: any, index: number) => (
                <tr key={row.id} className="hover:bg-gray-50" onClick={() => navigate(`/ShiftRoster/${row.id}`)}>
                  <td className="px-4 py-3 border border-[#e4e9f0] text-sm text-gray-700">{(currentPage - 1) * 10 + index + 1}</td>
                  <td className="px-6 py-3 border border-[#e4e9f0] text-sm text-gray-700">{row.reference_number}</td>

                  <td className="px-6 py-3 border border-[#e4e9f0] text-sm text-gray-700">{getMonthYear(row.month, row.year)}</td>

                  <td className="px-6 py-3 border border-[#e4e9f0] text-sm text-gray-700">
                    {new Date(row.created_at).toLocaleDateString('en-GB')}
                  </td>

                  <td className="px-6 py-3 border border-[#e4e9f0]">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">{row.compiled_by}</span>
                    </div>
                  </td>

                  <td className="px-6 py-3 border border-[#e4e9f0]">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${row.status ? 'bg-green-500' : 'bg-yellow-500'}`} />
                      <span className={`text-sm ${row.status ? 'text-green-500' : 'text-yellow-500'}`}>{row.status || 'Draft'}</span>
                    </div>
                  </td>

                  <td className="px-6 py-3 border border-[#e4e9f0]">
                    {row.status === 'New' && (
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={(event) => openDeleteConfirmation(event, row)}
                          className="text-red-500 hover:text-red-700"
                          aria-label="Delete new shift roster"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>

                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/ShiftRoster/${row.id}`);
                          }}
                          className="text-gray-400 hover:text-blue-500"
                          aria-label="Edit new shift roster"
                          title="Edit"
                        >
                          <SquarePen size={18} />
                        </button>
                      </div>
                    )}

                    {row.status === 'Approved' && (
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={(event) => openDownloadConfirmation(event, row)}
                          disabled={downloadingRosterId !== null}
                          className="text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label="Download approved shift roster"
                          title="Download"
                        >
                          <FileText size={18} className={downloadingRosterId === row.id ? 'animate-pulse' : ''} />
                        </button>

                        <button
                          type="button"
                          onClick={(event) => handlePrint(event, row)}
                          disabled={printingRosterId !== null}
                          className="text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label="Print approved shift roster"
                          title="Print"
                        >
                          <Printer size={18} className={printingRosterId === row.id ? 'animate-pulse' : ''} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      <ErrorModal isOpen={Boolean(downloadError)} onClose={() => setDownloadError('')} title="Download Failed" message={downloadError} />

      <ConfirmModal
        isOpen={Boolean(rosterToDownload)}
        title="Download Shift Roster"
        message="Are you sure you want to download this shift roster?"
        confirmText="Yes, Download"
        loadingText="Downloading..."
        cancelText="Cancel"
        isLoading={downloadingRosterId !== null}
        onCancel={() => setRosterToDownload(null)}
        onConfirm={handleDownload}
      />

      <ConfirmModal
        isOpen={Boolean(rosterToDelete)}
        title="Delete Shift Roster"
        message="Are you sure you want to delete this shift roster? This action cannot be undone."
        confirmText="Yes, Delete"
        loadingText="Deleting..."
        cancelText="Cancel"
        isLoading={deleteLoading}
        onCancel={() => setRosterToDelete(null)}
        onConfirm={handleDelete}
      />

      <ErrorModal isOpen={Boolean(deleteError)} onClose={() => setDeleteError('')} title="Delete Failed" message={deleteError} />

      <ErrorModal isOpen={Boolean(printError)} onClose={() => setPrintError('')} title="Print Failed" message={printError} />

      {printingRosterId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="flex items-center gap-3 rounded-lg bg-white px-6 py-4 shadow-lg">
            <Loader2 className="h-5 w-5 animate-spin text-red-500" />
            <span className="text-sm font-medium text-gray-700">Preparing print preview...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftRoasterTable;
